/**
 * 언어 선택 규칙 — 랜딩(`src/pages/landing/landingRuntime.js`)의 판별을 앱 전체 규칙으로 넓힌 순수 함수 모음.
 * 기획: `docs/ops/app-i18n-plan.md` §3.
 *
 * 우선순위: ?lang= → 계정(user.preferred_lang) → 이 브라우저에서 고른 값 → 기기 언어 → 한국어.
 * **자동 판별 결과는 저장하지 않는다** — 저장하면 나중에 기기 언어를 바꿔도 다시 판별하지 않는다.
 * (랜딩은 자동 결과까지 저장해 그 성질을 갖는다. 앱은 직접 고른 값만 기억한다.)
 */

export const SUPPORTED_LANGS = ["ko", "en", "ja", "zh"];
export const DEFAULT_LANG = "ko";

/** 랜딩과 **같은 키** — 로그인 전에 고른 언어가 로그인 후에도 그대로 이어진다. */
export const LANG_STORAGE_KEY = "laeebly_lang";

/** `<html lang>` 에 넣는 값. 중국어는 간체(zh-CN) — 번체 사전은 두지 않는다(랜딩과 같은 판단). */
export const HTML_LANG = { ko: "ko", en: "en", ja: "ja", zh: "zh-CN" };

/** BCP-47 태그 → 지원 언어. 못 알아보면 null. `zh-TW`/`zh-HK`(번체)도 zh 로 본다. */
export function normalizeLang(tag) {
  if (!tag) return null;
  const t = String(tag).toLowerCase();
  if (t.startsWith("ko")) return "ko";
  if (t.startsWith("ja")) return "ja";
  if (t.startsWith("zh")) return "zh";
  if (t.startsWith("en")) return "en";
  return null;
}

/**
 * 기기(브라우저) 설정 언어. `navigator.languages` 를 **사용자가 매긴 선호 순서대로** 훑는다.
 * 지원 언어가 하나도 없으면 영어 — 한국어보다 읽힐 확률이 높다. 목록 자체가 없으면 한국어.
 */
export function detectDeviceLang(languages) {
  const list = (Array.isArray(languages) ? languages : []).filter(Boolean);
  for (const raw of list) {
    const found = normalizeLang(raw);
    if (found) return found;
  }
  return list.length ? "en" : DEFAULT_LANG;
}

/** `?lang=en` 처럼 주소에 박힌 언어. 지원하지 않는 값은 무시한다. */
export function langFromSearch(search) {
  const m = /[?&]lang=(ko|en|ja|zh)\b/.exec(String(search || ""));
  return m ? m[1] : null;
}

/**
 * 첫 화면 언어를 정한다.
 * @returns {{ lang: string, source: "query"|"account"|"stored"|"device"|"default" }}
 *   source 는 화면에서 「기기 언어에 맞춰 골랐습니다」 안내를 띄울지 판단하는 데 쓴다.
 */
export function resolveInitialLang({ search, languages, stored, accountLang } = {}) {
  const q = langFromSearch(search);
  if (q) return { lang: q, source: "query" };

  const account = normalizeLang(accountLang);
  if (account) return { lang: account, source: "account" };

  const saved = normalizeLang(stored);
  if (saved) return { lang: saved, source: "stored" };

  const list = Array.isArray(languages) ? languages : [];
  if (list.length) return { lang: detectDeviceLang(list), source: "device" };

  return { lang: DEFAULT_LANG, source: "default" };
}

/**
 * 로그인 순간의 승계 — 이게 없으면 사용자가 언어를 두 번 고른다.
 * - 계정에 값이 있으면 그 값이 이긴다(다른 기기에서 고른 것).
 * - 계정이 비어 있고 이 브라우저에서 고른 값이 있으면 그 값을 계정에 옮겨 적는다.
 * - 둘 다 없으면 아무것도 하지 않는다(자동 판별 결과는 계정에 저장하지 않는다).
 * @returns {{ lang: string|null, saveToAccount: string|null }}
 */
export function resolveLoginLang({ accountLang, stored } = {}) {
  const account = normalizeLang(accountLang);
  if (account) return { lang: account, saveToAccount: null };
  const saved = normalizeLang(stored);
  if (saved) return { lang: saved, saveToAccount: saved };
  return { lang: null, saveToAccount: null };
}

/* ── 브라우저 저장소 (사파리 프라이빗 등에서 접근이 막힐 수 있어 전부 try/catch) ── */

export function readStoredLang() {
  try {
    return normalizeLang(window.localStorage.getItem(LANG_STORAGE_KEY));
  } catch (e) {
    return null;
  }
}

export function writeStoredLang(lang) {
  const next = normalizeLang(lang);
  if (!next) return;
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, next);
  } catch (e) {
    /* 저장 못 해도 이번 세션 동안은 화면이 바뀐다 */
  }
}
